import { withImageOptimizationQuery } from '@common/utils';

import { DEFAULT_DROPDOWN_INDEX, FAST_DELIVERY_TYPES } from '../constant/product-option';
import type {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matching_catalog_product_info as MatchingCatalogProductInfo,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_option_list as ProductOptionList,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_product_option_list_value_list as OptionValueList,
  PdpCatalogFulfillmentType,
} from '../graphql/select-option_types.2';
import { PdpCatalogSalesStatus, PdpDeliveryType } from '../graphql/select-option_types.2';
import type {
  ItemListMapValueType,
  OptionData,
  OptionInfo,
  ProductOptionConfig,
  SelectBoxDropdownItem,
  SelectBoxItemData,
} from '../type/common.type';
import { SelectionState } from '../type/common.type';
import {
  checkZigzinOrVendorFlexDeliveryType,
  checkZigzinOrVendorFlexFulfillmentType,
} from '../util/checkers/zigzin-checker';
import { parseShippingType } from '../util/transformers/shipping-type-transformer';
import type {
  CreateAdditionalOptionsParams,
  CreateOptionsParams,
  OptionProcessContext,
  ProcessOptionsParams,
} from './select-box-builder.core.type';

// 옵션 생성
export const createOptions = (params: CreateOptionsParams) => {
  const { option_info, option_map, selection, is_only_zigzin_mode, restock_selection_mode_activated_index } = params;

  if (!option_map?.item_list_map || !option_map.path_index_map || !option_info) {
    console.error('initialize 함수를 먼저 호출해주세요');
    return [];
  }

  const { product_option_list, matching_catalog_product_info } = option_info.catalog_product;
  const { selected_dropdown_index } = selection;

  if (selected_dropdown_index >= product_option_list.length) {
    return [];
  }

  let select_box_item_map: Map<string, SelectBoxItemData>;

  if (shouldShowZigzinOnly(is_only_zigzin_mode, matching_catalog_product_info)) {
    select_box_item_map = createZigzinOnlyModeOptions(params);
  } else {
    select_box_item_map = createMixedModeOptions(params);
  }

  return createOptionDropdownArray(
    product_option_list,
    selection,
    select_box_item_map,
    restock_selection_mode_activated_index,
    option_info.catalog_product.promotion_info?.bogo_info?.required_quantity ?? null,
  );
};

// 추가상품 옵션 생성
export const createAdditionalOptions = (params: CreateAdditionalOptionsParams) => {
  const { option_info, is_item_card_mode_active, config, handleOptionSelect } = params;

  if (!option_info) {
    console.error('initialize 함수를 먼저 호출해주세요');
    return [];
  }

  const { additional_item_list, shipping_fee } = option_info.catalog_product;

  if (!additional_item_list || additional_item_list.length === 0) {
    return [];
  }

  const select_box_item_data_list: SelectBoxItemData[] = additional_item_list.map((item, index) => {
    const is_sold_out = item.sales_status !== PdpCatalogSalesStatus.ON_SALE;
    const { name, value } = item.item_attribute_list?.[0] || {};

    const converted_data: SelectBoxItemData = {
      preview: {
        code: item.item_code || '',
        image_url: null,
        webp_image_url: null,
      },
      option: {
        id: item.id,
        name: `${name} ${value}`,
        price: item.price,
        display_price: item.price, // 추가상품은 항상 최종 가격 표시
        shipping: {
          type: null, // 추가상품은 배송 타입 미노출이므로 null로 처리
          fee: shipping_fee?.base_fee ?? null,
        },
        stock_status: {
          is_sold_out: is_sold_out,
          remain_stock_count: null, // additional_item_list에는 remain_stock_count가 없음
        },
      },
      action: {
        handleSelect: (option) => handleOptionSelect(option, index),
      },
    };

    return converted_data;
  });

  const ADDITIONAL_OPTION_TEXT = '[선택] 추가상품 선택';

  // 옵션 드롭다운이 숨겨진 경우 조건부 활성화
  // hidden_flags.option_dropdown = true이고 추가옵션이 있으면 즉시 활성화
  const should_enable_when_hidden = shouldEnableAdditionalOptionsWhenHidden(option_info, config || {});
  const is_enabled = is_item_card_mode_active || should_enable_when_hidden;

  const result: SelectBoxDropdownItem[] = [
    {
      selected_option_name: is_enabled ? ADDITIONAL_OPTION_TEXT : '',
      placeholder: ADDITIONAL_OPTION_TEXT,
      data: select_box_item_data_list,
      disabled: !is_enabled,
    },
  ];
  return result;
};

// 직진 옵션만 표시할지 여부 판단
const shouldShowZigzinOnly = (
  is_only_zigzin_mode: boolean,
  matching_catalog_product_info: MatchingCatalogProductInfo | null | undefined,
): boolean => {
  return is_only_zigzin_mode && !!matching_catalog_product_info;
};

// 직진 전용 모드 옵션 생성기
const createZigzinOnlyModeOptions = (params: CreateOptionsParams): Map<string, SelectBoxItemData> => {
  const {
    option_info,
    option_map,
    selection,
    restock_selection_mode_activated_index,
    hiddenRestockNotiButton,
    handleOptionSelect,
    handleOptionImpress,
    handleRestockNotification,
    createBaseSelectBoxItemData,
  } = params;
  const { fulfillment_type, product_option_list, matching_catalog_product_info } = option_info.catalog_product;
  const { item_list_map, path_index_map } = option_map;
  const { metadata, selected_group_index, selected_dropdown_index } = selection;

  if (!item_list_map || !path_index_map) {
    return new Map();
  }

  const is_zigzin_fulfillment = checkZigzinOrVendorFlexFulfillmentType(fulfillment_type);
  const selected_option_path = metadata.selected_option_name_list.join('/');

  const current_option = is_zigzin_fulfillment
    ? product_option_list?.[selected_dropdown_index]
    : matching_catalog_product_info?.option_list?.[selected_dropdown_index];

  return processOptionDataTransformation({
    context: {
      selected_option_path,
      selected_group_index,
      selected_dropdown_index,
      total_option_count: product_option_list.length,
      product_fulfillment_type: option_info.catalog_product.fulfillment_type,
      shipping_fee: option_info.catalog_product.shipping_fee?.base_fee ?? null,
      matching_shipping_fee: matching_catalog_product_info?.shipping_fee?.base_fee ?? null,
      restock_selection_mode_activated_index,
      hiddenRestockNotiButton,
    },
    data_sources: {
      value_list: current_option?.value_list,
      matching_value_list: matching_catalog_product_info?.option_list?.[selected_dropdown_index]?.value_list,
      item_list_map: item_list_map.zigzin,
      path_index_map: path_index_map.zigzin,
    },
    handlers: { handleOptionSelect, handleOptionImpress, handleRestockNotification, createBaseSelectBoxItemData },
  });
};

// 혼합 모드 옵션 생성기 (직진 + 일반배송)
const createMixedModeOptions = (params: CreateOptionsParams): Map<string, SelectBoxItemData> => {
  const {
    option_info,
    option_map,
    selection,
    restock_selection_mode_activated_index,
    hiddenRestockNotiButton,
    handleOptionSelect,
    handleOptionImpress,
    handleRestockNotification,
    createBaseSelectBoxItemData,
  } = params;
  const {
    fulfillment_type,
    product_option_list,
    matching_catalog_product_info,
    matched_item_list,
    is_only_zigzin_button_visible,
    promotion_info,
  } = option_info.catalog_product;
  const { item_list_map, path_index_map } = option_map;
  const { metadata, selected_group_index, selected_dropdown_index } = selection;

  if (!item_list_map || !path_index_map) {
    return new Map();
  }

  const is_bogo_product = (promotion_info?.bogo_info?.required_quantity ?? 0) > 0;
  const is_zigzin_fulfillment = checkZigzinOrVendorFlexFulfillmentType(fulfillment_type);
  const selected_option_path = metadata.selected_option_name_list.join('/');

  const is_matched_item_list_includes_fast_delivery = matched_item_list.some(
    (o) => o.delivery_type === PdpDeliveryType.FAST,
  );

  // 배송 타입을 null로 설정할지 여부 확인
  const should_nullify_shipping_type =
    !is_only_zigzin_button_visible && !is_zigzin_fulfillment && !is_matched_item_list_includes_fast_delivery;

  return processOptionDataTransformation({
    context: {
      selected_option_path,
      selected_group_index,
      selected_dropdown_index,
      total_option_count: product_option_list.length,
      product_fulfillment_type: fulfillment_type,
      restock_selection_mode_activated_index,
      should_nullify_shipping_type,
      shipping_fee: option_info.catalog_product.shipping_fee?.base_fee ?? null,
      matching_shipping_fee: matching_catalog_product_info?.shipping_fee?.base_fee ?? null,
      is_bogo_product,
      hiddenRestockNotiButton,
    },
    data_sources: {
      value_list: product_option_list?.[selected_dropdown_index]?.value_list,
      matching_value_list: matching_catalog_product_info?.option_list?.[selected_dropdown_index]?.value_list,
      item_list_map: item_list_map.matched,
      path_index_map: path_index_map.matched,
    },
    handlers: { handleOptionSelect, handleOptionImpress, handleRestockNotification, createBaseSelectBoxItemData },
  });
};

/**
 * 옵션 데이터를 SelectBox 아이템으로 변환하는 핵심 함수
 *
 * 1. 메인 상품 value_list를 순회하며 각 옵션 값에 대응하는 상품 옵션을 조회
 * 2. 직진배송/일반배송 매칭 옵션이 있는 경우 적절한 옵션 선택
 * 3. 직진 옵션 우선순위 처리 (판매중인 직진 옵션이 있으면 우선 표시)
 * 4. SelectBox UI에 필요한 데이터 구조로 변환
 *
 * @returns 옵션명을 key로 하는 SelectBoxItemData Map
 */
const processOptionDataTransformation = (params: ProcessOptionsParams): Map<string, SelectBoxItemData> => {
  const { context, data_sources, handlers } = params;
  const { item_list_map, path_index_map, value_list, matching_value_list } = data_sources;
  const { selected_dropdown_index, selected_option_path, product_fulfillment_type } = context;
  const { handleOptionSelect, handleOptionImpress, handleRestockNotification, createBaseSelectBoxItemData } = handlers;

  const select_box_item_map: Map<string, SelectBoxItemData> = new Map();

  if (!path_index_map || !value_list || value_list.length === 0) {
    return select_box_item_map;
  }

  const matchingValueLookup = buildMatchingValueLookup(matching_value_list, value_list);

  value_list.forEach((value) => {
    // STEP 1: 판매 가능한 최적의 상품 데이터 조회 (직진/일반 재고 상태 고려)
    const { item, matchingValue } = findBestAvailableItem(
      value,
      matchingValueLookup,
      selected_dropdown_index,
      product_fulfillment_type,
      item_list_map,
      path_index_map,
      selected_option_path,
    );

    // STEP 2: UI에 표시할 옵션 텍스트 결정 (선택된 상품의 배송 타입에 맞는 옵션명 사용)
    const targetValue = getTargetOptionValue(value, matchingValue, item, product_fulfillment_type);

    if (!item || !targetValue) return;

    // STEP 3: 맵 업데이트 여부 결정 (신규 추가 또는 직진 옵션으로 교체)
    const optionValue = targetValue.value;
    const existingItem = select_box_item_map.get(optionValue);

    if (!shouldUpdateSelectBoxItemMap(existingItem, item)) return;

    // STEP 4: SelectBox 아이템 데이터 생성 및 저장
    const selectBoxItemData = createSelectBoxItemData(targetValue, item, context, {
      handleOptionSelect,
      handleOptionImpress,
      handleRestockNotification,
    });

    const finalData = createBaseSelectBoxItemData
      ? createBaseSelectBoxItemData(selectBoxItemData, (data) => data)
      : selectBoxItemData;

    select_box_item_map.set(optionValue, finalData);
  });

  return select_box_item_map;
};

interface MatchingValueLookup {
  exact: Map<string, OptionValueList>;
  caseInsensitive: Map<string, OptionValueList>;
}

/**
 * 매칭 옵션 값 조회를 위한 lookup 구조 생성
 *
 * exact: 정확한 value 매칭 (O(1))
 * caseInsensitive: 대소문자 무시 fallback (O(1))
 *   - value_list 또는 matching_value_list 어느 쪽이든 같은 소문자 키에
 *     서로 다른 표기가 존재하면(예: FREE/free) 해당 키의 fallback을 비활성화
 */
export const buildMatchingValueLookup = (
  matchingValueList: OptionValueList[] | undefined,
  valueList: OptionValueList[],
): MatchingValueLookup => {
  const exact = new Map(matchingValueList?.map((v) => [v.value, v]) ?? []);
  const caseInsensitive = new Map<string, OptionValueList>();
  const ambiguousKeys = new Set<string>();

  const detectAmbiguous = (list?: OptionValueList[]) => {
    const seen = new Map<string, string>();
    list?.forEach((v) => {
      const key = v.value.toLowerCase();
      const prev = seen.get(key);
      if (prev !== undefined && prev !== v.value) {
        ambiguousKeys.add(key);
      }
      seen.set(key, v.value);
    });
  };

  detectAmbiguous(matchingValueList);
  detectAmbiguous(valueList);

  matchingValueList?.forEach((v) => {
    const key = v.value.toLowerCase();
    if (!ambiguousKeys.has(key)) {
      caseInsensitive.set(key, v);
    }
  });

  return { exact, caseInsensitive };
};

export const resolveMatchingValue = (optionValue: string, lookup: MatchingValueLookup): OptionValueList | undefined => {
  return lookup.exact.get(optionValue) ?? lookup.caseInsensitive.get(optionValue.toLowerCase());
};

/**
 * 하나의 옵션 값(예: Gray)에 대해 서로 다른 배송 타입(직진/일반)의 상품 데이터가 동시에 존재할 때,
 * 사용자에게 어떤 배송 타입의 아이템을 우선적으로 보여줄지 결정하는 역할을 합니다.
 *
 * 기본 원칙: 현재 보고 있는 상품 페이지의 성격(직진 상품인지 일반 상품인지)을 따릅니다.
 *    기본: 직진 상품 페이지라면 -> 직진 옵션 우선
 *    기본: 일반 상품 페이지라면 -> 일반 옵션 우선
 * 기본예외 처리 (재고 고려):
 *    기본우선순위인 옵션이 품절이고, 차순위 옵션은 구매 가능하다면 -> 구매 가능한 차순위 옵션을 보여줍니다.
 *    서로 다른 배송 타입인 경우 (예: 직진 상품 페이지인데 일반 배송 옵션만 있거나, 그 반대인 경우)에는 존재하는 옵션을 그대로 사용합니다.
 */
const findBestAvailableItem = (
  value: OptionValueList,
  matchingValueLookup: MatchingValueLookup,
  selectedDropdownIndex: number,
  productFulfillmentType: PdpCatalogFulfillmentType,
  itemListMap: Map<string, ItemListMapValueType>,
  pathIndexMap: Map<string, ItemListMapValueType> | null,
  selectedOptionPath: string,
): { item: ItemListMapValueType | null | undefined; matchingValue: OptionValueList | undefined } => {
  const matchingValue = resolveMatchingValue(value.value, matchingValueLookup);

  // 하위 옵션: 경로 기반 조회
  if (selectedDropdownIndex !== 0) {
    const item =
      pathIndexMap?.get(`${selectedOptionPath}/${value.value}`) ??
      (matchingValue ? pathIndexMap?.get(`${selectedOptionPath}/${matchingValue.value}`) : undefined);

    return { item: item ?? null, matchingValue };
  }

  // 첫 번째 옵션: 직진/일반 매칭 옵션 우선순위 결정
  const item = resolveFirstOptionItem(value, matchingValue, productFulfillmentType, itemListMap);
  return { item, matchingValue };
};

/**
 * 첫 번째 옵션에서 직진/일반 배송 타입에 따라 최적의 아이템을 선택
 *
 * 우선순위:
 *   1. 같은 배송 타입의 재고 있는 옵션
 *   2. 다른 배송 타입이지만 재고 있는 옵션 (fallback)
 *   3. 존재하는 아무 옵션
 */
export const resolveFirstOptionItem = (
  value: OptionValueList,
  matchingValue: OptionValueList | undefined,
  productFulfillmentType: PdpCatalogFulfillmentType,
  itemListMap: Map<string, ItemListMapValueType>,
): ItemListMapValueType | null | undefined => {
  const mainOption = itemListMap.get(value.id);
  const matchingOption = itemListMap.get(matchingValue?.id ?? '');

  const isSameShippingType =
    parseShippingType(productFulfillmentType) === parseShippingType(mainOption?.delivery_type ?? null);

  // 배송 타입이 다른 경우: 존재하는 옵션 사용
  if (!isSameShippingType) {
    return mainOption ?? matchingOption;
  }

  const isZigzinProduct = checkZigzinOrVendorFlexFulfillmentType(productFulfillmentType);
  const [primary, fallback] = isZigzinProduct ? [mainOption, matchingOption] : [matchingOption, mainOption];

  // primary가 품절이고 fallback이 판매중이면 fallback 사용
  const shouldUseFallback =
    primary &&
    fallback &&
    primary.sales_status !== PdpCatalogSalesStatus.ON_SALE &&
    fallback.sales_status === PdpCatalogSalesStatus.ON_SALE;

  return shouldUseFallback ? fallback : (primary ?? fallback);
};

/**
 * 표시할 옵션 값 선택
 *
 * 배송 타입이 일치하는 경우: 현재 value 사용 (예: 직진 상품에서 직진 옵션 선택 시)
 * 배송 타입이 불일치하는 경우: 매칭 옵션의 value 사용 (예: 직진 상품에서 일반 옵션 선택 시)
 *
 * Fallback 이유:
 * - matchingValueList가 없는 경우: 매칭 옵션이 없는 단일 배송 타입 상품
 * - 이 경우에도 옵션을 표시하기 위해 현재 value를 fallback으로 사용
 */
const getTargetOptionValue = (
  value: OptionValueList,
  matchingValue: OptionValueList | undefined,
  item: ItemListMapValueType | null | undefined,
  productFulfillmentType: PdpCatalogFulfillmentType,
): OptionValueList | undefined => {
  if (!item) return undefined;
  const isSameShippingType = parseShippingType(productFulfillmentType) === parseShippingType(item.delivery_type);
  return isSameShippingType ? value : matchingValue;
};

// 맵 업데이트 조건 확인
const shouldUpdateSelectBoxItemMap = (
  existingItem: SelectBoxItemData | undefined,
  item: ItemListMapValueType,
): boolean => {
  // 기존 아이템이 없는 경우 업데이트
  const isNewEntry = !existingItem;
  // 기존 아이템이 직진이 아니면서 직진 아이템이 있는 경우 업데이트
  const isZigzinPriority =
    !checkZigzinOrVendorFlexDeliveryType(existingItem?.option?.shipping?.type ?? null) &&
    checkZigzinOrVendorFlexDeliveryType(item.delivery_type) &&
    item.sales_status === PdpCatalogSalesStatus.ON_SALE;

  return isNewEntry || isZigzinPriority;
};

// 재입고 버튼 표시 조건 확인
const shouldShowRestockButton = (
  item: ItemListMapValueType,
  context: {
    isFirstOption: boolean;
    isLastOption: boolean;
    isRestockSelectionMode: boolean;
    isBogoProduct?: boolean;
  },
): boolean => {
  const isSoldOut = item.sales_status !== PdpCatalogSalesStatus.ON_SALE;
  const isZigzinDelivery = item.delivery_type === PdpDeliveryType.ZIGZIN;
  const canShowNotification = !context.isRestockSelectionMode || context.isFirstOption || context.isLastOption;

  return isSoldOut && isZigzinDelivery && canShowNotification && !context.isBogoProduct;
};

// 옵션 데이터 생성
const createSelectBoxItemData = (
  value: OptionValueList,
  item: ItemListMapValueType,
  context: OptionProcessContext,
  handlers: {
    handleOptionSelect: (option: OptionData, groupIndex: number, dropdownIndex: number) => void;
    handleOptionImpress: (option: OptionData, isRestockButtonActive: boolean) => void;
    handleRestockNotification: (option: OptionData, dropdownIndex: number) => void;
  },
): SelectBoxItemData => {
  const { handleOptionSelect, handleOptionImpress, handleRestockNotification } = handlers;
  const isFirstOption = context.selected_dropdown_index === 0;
  const isLastOption = context.selected_dropdown_index === context.total_option_count - 1;
  const isRestockSelectionMode = Number.isInteger(context.restock_selection_mode_activated_index);

  const isSoldOut = item.sales_status !== PdpCatalogSalesStatus.ON_SALE;
  const shouldShowRestock = context.hiddenRestockNotiButton
    ? false
    : shouldShowRestockButton(item, {
        isFirstOption,
        isLastOption,
        isRestockSelectionMode,
        isBogoProduct: context.is_bogo_product,
      });

  return {
    preview: {
      code: value.code,
      image_url: withImageOptimizationQuery(value.jpeg_url, { width: 120, height: 120 }),
      webp_image_url: withImageOptimizationQuery(value.static_url, { width: 120, height: 120 }),
    },
    option: {
      id: value.id,
      name: value.value,
      price: item.price,
      display_price: getDisplayPrice(item, isSoldOut, isLastOption, context.is_bogo_product),
      shipping: {
        type: getShippingType(item, context.should_nullify_shipping_type),
        fee: context.shipping_fee ?? null,
      },
      stock_status: {
        is_sold_out: isSoldOut,
        remain_stock_count: getRemainStockCount(item, isLastOption, context.is_bogo_product),
      },
    },
    restock_button: shouldShowRestock
      ? {
          button_type: isLastOption ? 'notification' : 'view_more',
          active: !!item?.wms_notification_info?.active,
        }
      : undefined,
    ui_setting: {
      display_style: getDisplayStyle(isRestockSelectionMode),
    },
    action: {
      handleSelect: (option) =>
        handleOptionSelect(option, context.selected_group_index, context.selected_dropdown_index),
      handleImpress: (option, isRestockButtonActive) => handleOptionImpress(option, isRestockButtonActive),
      handleRestockNotification: (option) => handleRestockNotification(option, context.selected_dropdown_index),
    },
  };
};

const getDisplayPrice = (
  item: ItemListMapValueType,
  isSoldOut: boolean,
  isLastOption: boolean,
  isBogoProduct?: boolean,
): number | null => {
  return !isSoldOut && isLastOption && !isBogoProduct ? item.final_price : null;
};

const getShippingType = (item: ItemListMapValueType, shouldNullifyShippingType?: boolean): PdpDeliveryType | null => {
  return shouldNullifyShippingType ? null : item.delivery_type || PdpDeliveryType.GENERAL;
};

const getRemainStockCount = (
  item: ItemListMapValueType,
  isLastOption: boolean,
  isBogoProduct?: boolean,
): number | null => {
  return isLastOption && !isBogoProduct ? item.remain_stock : null;
};

const getDisplayStyle = (isRestockSelectionMode: boolean): 'default' | 'restock_notification_selection' => {
  return isRestockSelectionMode ? 'restock_notification_selection' : 'default';
};

// 옵션 선택 드롭다운을 위한 결과 배열 생성
export const createOptionDropdownArray = (
  option_list: ProductOptionList[],
  selection: SelectionState,
  option_map: Map<string, SelectBoxItemData>,
  restock_selection_mode_activated_index: number | null,
  bogo_required_quantity: number | null,
): SelectBoxDropdownItem[][] => {
  const { selected_group_index, selected_dropdown_index, metadata } = selection;

  if (selected_dropdown_index === DEFAULT_DROPDOWN_INDEX) {
    return [
      [
        {
          selected_option_name: '기본상품 선택',
          placeholder: '기본상품 선택',
          data: [],
          disabled: false,
          display: true,
        },
      ],
    ];
  }

  const restock_min_index = restock_selection_mode_activated_index ?? -1;
  const group_count = (bogo_required_quantity ?? 0) + 1;

  return Array.from({ length: group_count }, (_, group_index) => {
    if (group_index < selected_group_index) {
      return buildCompletedGroupDropdowns(
        option_list,
        metadata.previous_group_selections?.[group_index]?.selected_option_name_list ?? [],
        restock_min_index,
      );
    }

    if (group_index > selected_group_index) {
      return buildDisabledGroupDropdowns(option_list);
    }

    return buildActiveGroupDropdowns(
      option_list,
      selected_dropdown_index,
      metadata.selected_option_name_list,
      option_map,
      restock_min_index,
    );
  });
};

// 선택 완료된 이전 그룹: 선택된 옵션명 표시
const buildCompletedGroupDropdowns = (
  option_list: ProductOptionList[],
  selected_names: string[],
  restock_min_index: number,
): SelectBoxDropdownItem[] =>
  selected_names.map((name, i) => ({
    selected_option_name: name,
    placeholder: option_list[i]?.name,
    data: [],
    display: i > restock_min_index, // 재입고 알림 모드의 경우 활성화 인덱스 이하는 비활성화
  }));

// 미래 그룹: 모든 옵션 비활성화
const buildDisabledGroupDropdowns = (option_list: ProductOptionList[]): SelectBoxDropdownItem[] =>
  option_list.map((opt) => ({
    placeholder: opt.name,
    data: [],
    disabled: true,
    display: true,
  }));

// 현재 활성 그룹: 완료된 단계 + 현재 단계(옵션 데이터 포함) + 미래 단계
const buildActiveGroupDropdowns = (
  option_list: ProductOptionList[],
  selected_dropdown_index: number,
  selected_option_names: string[],
  option_map: Map<string, SelectBoxItemData>,
  restock_min_index: number,
): SelectBoxDropdownItem[] =>
  option_list.map((opt, i) => {
    if (i < selected_dropdown_index) {
      return {
        selected_option_name: selected_option_names[i],
        placeholder: opt.name,
        data: [],
        disabled: false,
        display: i > restock_min_index, // 재입고 알림 모드의 경우 활성화 인덱스 이하는 비활성화
      };
    }

    if (i === selected_dropdown_index) {
      return {
        placeholder: opt.name,
        data: sortOptionsByPriority(option_map),
        disabled: false,
        display: true,
      };
    }

    return {
      placeholder: opt.name,
      data: [],
      disabled: true,
      display: true,
    };
  });

/**
 * 옵션 우선순위에 따른 정렬
 * 품절 여부 (재고 있는 것 우선)
 */
const sortOptionsByPriority = (options: Map<string, SelectBoxItemData>): SelectBoxItemData[] => {
  const options_array = Array.from(options.values());

  return options_array.sort((a, b) => {
    // 품절 여부 비교 (품절 아이템을 뒤로)
    const is_sold_out_a = a.option?.stock_status?.is_sold_out ?? false;
    const is_sold_out_b = b.option?.stock_status?.is_sold_out ?? false;

    if (is_sold_out_a !== is_sold_out_b) {
      return is_sold_out_a ? 1 : -1;
    }

    // 배송 타입 우선순위 비교 (직진/벤더플렉스를 앞으로)
    const is_fast_delivery_a = FAST_DELIVERY_TYPES.includes(a.option?.shipping?.type ?? PdpDeliveryType.GENERAL);
    const is_fast_delivery_b = FAST_DELIVERY_TYPES.includes(b.option?.shipping?.type ?? PdpDeliveryType.GENERAL);

    if (is_fast_delivery_a !== is_fast_delivery_b) {
      return is_fast_delivery_a ? -1 : 1;
    }

    // 모든 조건이 동일하면 순서 유지
    return 0;
  });
};

/**
 * 옵션 드롭다운이 숨겨진 경우 추가옵션을 활성화해야 하는지 판단
 * @param option_info - 옵션 정보
 * @param config - 설정 정보
 * @returns 추가옵션 활성화 필요 여부
 */
const shouldEnableAdditionalOptionsWhenHidden = (
  option_info: OptionInfo | null,
  config: ProductOptionConfig,
): boolean => {
  // 옵션 드롭다운이 숨겨지지 않은 경우
  if (!config.hidden_flags?.option_dropdown) {
    return false;
  }

  // 추가옵션 드롭다운도 숨겨진 경우
  if (config.hidden_flags?.additional_option_dropdown) {
    return false;
  }

  // 옵션 정보가 없는 경우
  if (!option_info) {
    return false;
  }

  // 추가 옵션이 있는 경우에만 활성화 필요
  const has_additional_options = Boolean(option_info.catalog_product.additional_item_list?.length);

  return has_additional_options;
};
